/* dialog.js — shared <dialog> primitive.
 *
 * Centralises the create + append-to-body + backdrop-click-close plumbing that
 * each dialog used to re-implement by hand, plus declarative close wiring:
 * any element with a [data-close] attribute closes the dialog on click. Escape
 * is native to <dialog>.showModal(). Callers set the body HTML and wire their
 * own action buttons (anything that does more than close).
 *
 *   const dlg = createDialog("my-dialog");        // once; reuse across opens
 *   renderDialog(dlg, `<button data-close>OK</button>`);  // sets HTML + opens
 */

/**
 * Create a managed <dialog>: appended to <body>, closes when its ::backdrop is
 * clicked. Reuse the returned element across opens.
 * @param {string} [className]
 * @returns {HTMLDialogElement}
 */
export function createDialog(className = "") {
  const dlg = document.createElement("dialog");
  if (className) dlg.className = className;
  document.body.appendChild(dlg);
  dlg.addEventListener("click", (e) => { if (e.target === dlg) dlg.close(); });
  return dlg;
}

/**
 * Set the dialog's body HTML, auto-wire any [data-close] element to close it,
 * and open it modally if not already open. Returns the dialog.
 * @param {HTMLDialogElement} dlg
 * @param {string} html
 * @param {{open?: boolean}} [opts]
 * @returns {HTMLDialogElement}
 */
export function renderDialog(dlg, html, { open = true } = {}) {
  dlg.innerHTML = html;
  dlg.querySelectorAll("[data-close]").forEach((el) =>
    el.addEventListener("click", () => dlg.close()));
  if (open && !dlg.open) dlg.showModal();
  return dlg;
}
